// What a visit is worth in hours — the one implementation.
//
// Which window a visit is measured by. AHFC pays and bills on different bases
// (decided 2026-09-13), so this is one concept with one implementation rather
// than a rule restated at each call site.
//
//   scheduled             the window the visit was scheduled for, clocked or not
//   clocked               the window actually worked; no clock pair means no hours
//   clocked_else_scheduled  clocked where it exists, scheduled where it doesn't
//
// Payroll is always `clocked`: AHFC pays hours worked, and a visit with no
// clock pair is an exception to fix, not hours to pay. Billing is whichever
// basis its run was created with (default `clocked_else_scheduled`), because
// care delivered by a caregiver who forgot to clock is still care delivered.
//
// No Supabase, no DOM: edge functions import this through _shared/lib. The
// Monday billing run used to carry its own copy, which disagreed with this one
// about a clock pair that measures nothing (2026-10-19) — see below.

export const HOURS_BASES = [
  ['scheduled',              'Scheduled hours — bill the planned window, clocked or not'],
  ['clocked',                'Clocked hours — bill only what was clocked'],
  ['clocked_else_scheduled', 'Clocked where available, otherwise scheduled'],
];

const spanHours = (s, e) => {
  if (!s || !e) return 0;
  const h = (new Date(e) - new Date(s)) / 3600000;
  return isNaN(h) || h < 0 ? 0 : h;
};

// A clocked time reaches us two ways: clockIn/clockOut carry the method and
// location, actualStartDate/actualEndDate the bare timestamps. Both are set on
// a CAERA-native visit and on an AxisCare one, but different call sites have
// historically read different fields, so treat either as the clock record.
const clockIn  = (v) => v?.clockIn?.time  || v?.actualStartDate || null;
const clockOut = (v) => v?.clockOut?.time || v?.actualEndDate   || null;

/** Does this visit have both a clock-in and a clock-out, manual or automatic? */
export const hasClockPair = (v) => !!(clockIn(v) && clockOut(v));

/**
 * The clocked window, or null when there is no usable one. A pair whose
 * clock-out is not after its clock-in measures nothing and is treated as no
 * clock record at all — otherwise `clocked_else_scheduled` would value a
 * delivered visit at zero because of a typo in the clock, and drop it.
 */
export const clockWindow = (v) => {
  if (!hasClockPair(v)) return null;
  const start = new Date(clockIn(v)); const end = new Date(clockOut(v));
  if (isNaN(start) || isNaN(end) || end <= start) return null;
  return { start, end };
};

/** Hours for a visit on the given basis. Unknown bases fall back to scheduled. */
export const visitHoursOn = (v, basis = 'scheduled') => {
  const scheduled = spanHours(v?.scheduledStartDate || v?.startDate, v?.scheduledEndDate || v?.endDate);
  if (basis === 'scheduled') return scheduled;
  const w = clockWindow(v);
  const clocked = w ? (w.end - w.start) / 3600000 : 0;
  if (basis === 'clocked') return clocked;
  if (basis === 'clocked_else_scheduled') return w ? clocked : scheduled;
  return scheduled;
};
